import { centralEnabled } from './services/centralAuth';
import { CentralSignIn, CentralCallback, CentralGate, CentralMembers, CentralSiteAccount, CentralSiteSettings, CentralProfileLink, CentralCreateSite } from './frontend/user/CentralAccounts';
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import SignIn from './frontend/user/SignIn';
import SelectSite from './frontend/user/SelectSite';
import ProtectedRoute from './routes/ProtectedRoute';
import { AdminRoutes } from './routes/AdminRoutes';
import SiteAdminHome from './sites/pages/SiteAdminHome';
import AdminLayout from './sites/components/common/AdminLayout';
import Designer from './sites/pages/pagedesign/Designer';
import Menus from './sites/pages/menus/Menus';
import SiteHeaders from './sites/pages/headers/SiteHeaders';
import SiteFooters from './sites/pages/footers/SiteFooters';
import PrivacyNotices from './sites/pages/privacycookies/PrivacyNotices';
import ScriptsAndStyles from './sites/pages/scripts/ScriptsAndStyles';
import AccountDetails from './sites/pages/sitesettings/AccountDetails';
import AccountPaymentStatus from './sites/pages/sitesettings/AccountPaymentStatus';
import SiteDetails from './sites/pages/sitesettings/SiteDetails';
import SiteLanguages from './sites/pages/languages/SiteLanguages';
import MediaManager from './sites/pages/mediamanger/MediaManager';
import PageKeywords from './sites/pages/pagekeywords/PageKeywords';
import PageMetaData from './sites/pages/pagemetadata/PageMetaData';
import SiteMetaData from './sites/pages/sitemetadata/SiteMetaData';
import ManageRedirects from './sites/pages/redirects/ManageRedirects';
import SocialMedia from './sites/pages/socialmedia/SocialMedia';
import StaticPages from './sites/pages/staticpages/StaticPages';
import SubmittedData from './sites/pages/submitteddata/SubmittedData';
import ManageSubscribers from './sites/pages/subscribers/ManageSubscribers';
import ImportSubscribers from './sites/pages/subscribers/ImportSubscribers';
import Newsletters from './sites/pages/subscribers/Newsletters';
import SiteUsers from './sites/pages/users/SiteUsers';
import SystemAdminRoute from './routes/SystemAdminRoute';
import MyDetails from './sites/pages/users/MyDetails';
import ChangePassword from './sites/pages/users/ChangePassword';
import RegisterNewSite from './sites/pages/users/RegisterNewSite';
import AllComponents from './systemadmin/componentadmin/AllComponents';
import ComponentPropertyTypes from './systemadmin/componentadmin/ComponentPropertyTypes';
import LayoutTemplates from './systemadmin/componentadmin/LayoutTemplates';
import SocialMediaLinks from './sites/pages/socialmediaaccounts/SocialMediaLinks';
import TwitterCallback from './sites/components/common/TwitterCallback';
import CancelPasswordReset from './frontend/user/CancelPasswordReset';
import ChangePaswordFE from './frontend/user/ChangePassword';
import SignUp from './frontend/user/SignUp';
import ThemeProvider from './context/ThemeProvider';
import './App.scss';


// Helper function to normalize path (convert to lowercase)
const normalizePath = (path: string): string => path.toLowerCase();

// Move the location-based logic inside a Router context
const AppRoutes: React.FC = () => {
  const location = useLocation();

  // Normalize the current location pathname to lowercase
  const normalizedLocationPath = normalizePath(location.pathname);

  // Check if the normalized path matches any admin route
  const isSiteAdmin = AdminRoutes.some(route => normalizedLocationPath.startsWith(normalizePath(route)));

  return (
    <Routes>
      {/* Public Routes */}
      {!isSiteAdmin && (
        <>
          <Route path="/" element={<Navigate to="/signin" replace />} />
          <Route path="/signin" element={centralEnabled ? <CentralSignIn /> : <SignIn />} />
          <Route path="/select-site" element={<SelectSite />} />
          <Route path="/twitter-callback" element={<TwitterCallback />} />
          <Route path="/twittercallback" element={<TwitterCallback />} />
          <Route path="/cancelreset/:resetcode" element={<CancelPasswordReset />} />
          <Route path="/resetpassword/:resetcode" element={<ChangePaswordFE />} />
          <Route path="/join" element={centralEnabled ? <CentralProfileLink path="/register?product=cms" /> : <SignUp />} />
          <Route path="/auth/callback" element={centralEnabled ? <CentralCallback /> : <Navigate to="/signin" replace />} />
          {/* Other public routes */}
        </>
      )}

      {isSiteAdmin && (
        <Route
          path="*"
          element={
            <AdminLayout> {/* Wrap all admin routes inside the layout */}
              <Routes>
                <Route path="/content" element={<ProtectedRoute><SiteAdminHome /></ProtectedRoute>} />
                <Route path="/content/design/:siteid/:versionid" element={<ProtectedRoute><Designer /></ProtectedRoute>} />
                <Route path="/content/menus" element={<ProtectedRoute><Menus /></ProtectedRoute>} />
                <Route path="/content/headers" element={<ProtectedRoute><SiteHeaders /></ProtectedRoute>} />
                <Route path="/content/footers" element={<ProtectedRoute><SiteFooters /></ProtectedRoute>} />
                <Route path="/content/privacynotices" element={<ProtectedRoute><PrivacyNotices /></ProtectedRoute>} />
                <Route path="/content/scriptandcss" element={<ProtectedRoute><ScriptsAndStyles /></ProtectedRoute>} />
                <Route path="/content/languages" element={<ProtectedRoute><SiteLanguages /></ProtectedRoute>} />
                <Route path="/content/mediamanager" element={<ProtectedRoute><MediaManager /></ProtectedRoute>} />
                <Route path="/content/pagekeyword/:pageParamId?" element={<ProtectedRoute><PageKeywords /></ProtectedRoute>} />
                <Route path="/content/metadata/:pageParamId?" element={<ProtectedRoute><PageMetaData /></ProtectedRoute>} />
                <Route path="/content/sitemetadata" element={<ProtectedRoute><SiteMetaData /></ProtectedRoute>} />
                <Route path="/content/manageredirects" element={<ProtectedRoute><ManageRedirects /></ProtectedRoute>} />
                <Route path="/content/socialmedia" element={<ProtectedRoute><SocialMedia /></ProtectedRoute>} />
                <Route path="/content/staticroutes" element={<ProtectedRoute><StaticPages /></ProtectedRoute>} />
                <Route path="/content/submitteddata" element={<ProtectedRoute><SubmittedData /></ProtectedRoute>} />
                <Route path="/content/socialmedialinks" element={<ProtectedRoute><SocialMediaLinks /></ProtectedRoute>} />
                <Route path="/subscribers/manage" element={<ProtectedRoute><ManageSubscribers /></ProtectedRoute>} />
                <Route path="/subscribers/import" element={<ProtectedRoute><ImportSubscribers /></ProtectedRoute>} />
                <Route path="/subscribers/newsletters" element={<ProtectedRoute><Newsletters /></ProtectedRoute>} />
                <Route path="/users/all" element={<ProtectedRoute>{centralEnabled ? <CentralMembers /> : <SiteUsers />}</ProtectedRoute>} />
                <Route path="/users/mydetails" element={<ProtectedRoute>{centralEnabled ? <CentralProfileLink path="/profile" /> : <MyDetails />}</ProtectedRoute>} />
                <Route path="/users/changepassword" element={<ProtectedRoute>{<ChangePassword />}</ProtectedRoute>} />
                <Route path="/users/registernewsite" element={<ProtectedRoute>{centralEnabled ? <CentralCreateSite /> : <RegisterNewSite />}</ProtectedRoute>} />
                <Route path="/account/details" element={<ProtectedRoute>{centralEnabled ? <CentralSiteAccount /> : <AccountDetails />}</ProtectedRoute>} />
                <Route path="/account/paymentstatus" element={<ProtectedRoute>{centralEnabled ? <CentralSiteAccount /> : <AccountPaymentStatus />}</ProtectedRoute>} />
                <Route path="/account/sitedetails" element={<ProtectedRoute><CentralSiteSettings><SiteDetails /></CentralSiteSettings></ProtectedRoute>} />
                <Route path="/systemadmin/components" element={<SystemAdminRoute><AllComponents /></SystemAdminRoute>} />
                <Route path="/systemadmin/propertytypes" element={<SystemAdminRoute><ComponentPropertyTypes /></SystemAdminRoute>} /> 
                <Route path="/systemadmin/layouttemplates" element={<SystemAdminRoute><LayoutTemplates /></SystemAdminRoute>} /> 
              </Routes>
            </AdminLayout>
          }
        />
      )}

      {/* Default Fallback */}
      <Route path="*" element={<Navigate to="/signin" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <Router>
        <CentralGate shell={AdminRoutes.some(route => normalizePath(window.location.pathname).startsWith(normalizePath(route)))}><AppRoutes /></CentralGate>
      </Router>
    </ThemeProvider>
  );
};

export default App;
